import React, { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import noDataIcon from '../../assets/images/no_data.png';
import { formatCurrency } from '@utils/common';

export type Column<T> = {
  key: keyof T;
  label: string;
  render?: (value: T[keyof T], row: T, level?: number) => ReactNode;
  renderHeader?: (column: Column<T>) => ReactNode;
  secondaryColumns?: Column<T>[];
  customCellStyles?: any;
  customHeaderStyles?: any;
};

export type Row<T> = T & {
  id: string;
  children?: Row<T>[];
  getChildren?: (rowId: string) => Row<T>[];
};

export type RowAction = {
  label?: string;
  icon?: string;
  renderUpdateComponent?: any;
  condition?: (rowId: string) => boolean;
  disabled?: (rowId: string) => boolean;
  action?: (rowId: string, el?: any) => void;
  component?: ReactElement;
};

export type GenericTableProps<T> = {
  data: Row<T>[];
  columns: Column<T>[];
  meta: any;
  actions?: RowAction[];
  isSubLevelAction?: boolean;
  onViewRow?: (row: Row<T>) => void;
  isViewRowClickable?: (row: Row<T>) => boolean;
  isLoading?: boolean;
  depth?: number | null;
  showTotal?: boolean;
  customCurrency?: string;
  showCurrencyCode?: boolean;
  getCellBackground?: (value: T[keyof T], cellType: string) => string;
  initiallyExpanded?: boolean;
  isHeightRestricted?: boolean;
  hideExpandIfNoChildren?: boolean;
  alignActionsColumn?: 'left' | 'right' | 'center';
  enableRowSelect?: boolean;
  selections?: string[];
  setSelections?: (indices: string[]) => void;
  hideScrollbar?: boolean;
};

const ROW_COLORS = ['#D8DBDD', '#F0F0F1'];

function GenericTable<T>({
  data,
  columns,
  meta,
  actions,
  isSubLevelAction,
  onViewRow,
  isViewRowClickable,
  isLoading,
  depth,
  showTotal = false,
  customCurrency = 'USD',
  showCurrencyCode = false,
  getCellBackground,
  initiallyExpanded = false,
  isHeightRestricted = false,
  hideExpandIfNoChildren = false,
  alignActionsColumn = 'center',
  enableRowSelect = false,
  selections,
  setSelections,
  hideScrollbar = false,
}: GenericTableProps<T>) {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [allExpanded, setAllExpanded] = useState(false);
  const [updatingRowId, setUpdatingRowId] = useState<string | null>(null);
  const [dynamicNestedRows, setDynamicNestedRows] = useState<{
    index: number;
    dynamicRows: Row<T>[] | null;
  }>({ index: -1, dynamicRows: null });

  const [selectedRows, setSelectedRows] = useState<string[]>(selections || []);

  // Update internal state when selections prop changes
  useEffect(() => {
    if (selections) {
      setSelectedRows(selections);
    }
  }, [selections]);

  useEffect(() => {
    const allExpanded = isAllRowsExpanded(data);
    setAllExpanded(allExpanded);
  }, [expandedRows, data]);

  // Effect to expand all rows initially if initiallyExpanded is true
  useEffect(() => {
    if (initiallyExpanded && data.length > 0) {
      const updatedExpandedRows: Record<string, boolean> = {};

      const setAllRowState = (rows: Row<T>[]) => {
        rows.forEach((row) => {
          if (row.children && row.children.length > 0) {
            updatedExpandedRows[row.id] = true;
            setAllRowState(row.children);
          }
        });
      };

      setAllRowState(data);
      setExpandedRows(updatedExpandedRows);
      setAllExpanded(true);
    }
  }, [initiallyExpanded, data]);

  const isShowActionColumn = Boolean(actions?.length);

  // Hides the horizontal scrollbar while keeping the table scrollable
  const hiddenScrollbarStyles = hideScrollbar
    ? {
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { height: 0 },
      }
    : undefined;

  // Helper: check if any column has secondary columns
  const hasSecondaryColumns = useMemo(() => {
    return !!columns?.some((col) => col.secondaryColumns && col.secondaryColumns.length > 0);
  }, [columns]);

  // Helper: get all leaf columns (flattened columns including secondary columns)
  const getLeafColumns = useMemo(() => {
    const leafColumns: Column<T>[] = [];
    columns?.forEach((col) => {
      if (col.secondaryColumns && col.secondaryColumns.length > 0) {
        leafColumns.push(...col.secondaryColumns);
      } else {
        leafColumns.push(col);
      }
    });
    return leafColumns;
  }, [columns]);

  const columnMetadata = meta?.columns?.length
    ? meta.columns.reduce((obj: any, item: any) => {
        obj[item.id] = item;
        return obj;
      }, {})
    : [];

  const handleExpandAllRows = () => {
    const newExpandState = !allExpanded;
    setAllExpanded(newExpandState);

    const updatedExpandedRows: Record<string, boolean> = {};
    const setAllRowState = (rows: Row<T>[]) => {
      rows.forEach((row) => {
        updatedExpandedRows[row.id] = newExpandState;
        if (row.children) {
          setAllRowState(row.children);
        }
      });
    };
    setAllRowState(data);

    setExpandedRows(updatedExpandedRows);

    const updateRowsState = (rows: Row<T>[], isExpanded: boolean) => {
      rows.forEach((row) => {
        if (row.children) {
          updateRowsState(row.children, isExpanded);
        }
      });
    };
    updateRowsState(data, newExpandState);
  };

  const handleExpandRowClick = (
    id: string,
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.stopPropagation();
    setExpandedRows((prev) => {
      const isExpanded = !prev[id];
      return { ...prev, [id]: isExpanded };
    });
  };

  const handleExpandDynamicChildrenRowClick = async (rowIndex: number, row: any, e: any) => {
    e.stopPropagation();
    if (
      dynamicNestedRows?.dynamicRows?.length &&
      (dynamicNestedRows.dynamicRows[0].id as string).includes(row.id)
    ) {
      setDynamicNestedRows({ index: -1, dynamicRows: null });
      return;
    }
    setDynamicNestedRows({ index: -1, dynamicRows: null });

    if (row.getChildren) {
      const childRows = await row.getChildren(row.id);
      setDynamicNestedRows({
        index: rowIndex,
        dynamicRows: childRows,
      });
    }
  };

  const handleRowActionClick = (rowId: string, action: RowAction, el?: any) => {
    if (!action || !isShowActionColumn) return;

    const { renderUpdateComponent, action: actionHandler } = action;

    if (renderUpdateComponent) {
      setUpdatingRowId((prev) => (prev === rowId ? null : rowId));
    } else if (actionHandler) {
      if (el) {
        actionHandler(rowId, el);
      } else {
        actionHandler(rowId);
      }
    }
  };

  const isAllRowsExpanded = (rows: Row<T>[]): boolean => {
    for (const row of rows) {
      if (row.children) {
        if (!expandedRows[row.id] || !isAllRowsExpanded(row.children)) return false;
      }
    }
    return true;
  };

  const checkCellValueType = (type: string, value: any) => {
    switch (type) {
      case 'string':
        return value;
      case 'percentage':
        return Number(value) === 0 ? '-' : `${(Number(value) * 100).toFixed(2)}%`;
      case 'currency':
        return Number(value) === 0
          ? '-'
          : formatCurrency(value, false, 2, 2, { currency: customCurrency }, showCurrencyCode);
      case 'date':
        return value;
      default:
        return null;
    }
  };

  const getCellValueAllignment = (type: string) => {
    switch (type) {
      case 'percentage':
      case 'currency':
      case 'rightAligned':
        return 'right';
      case 'string':
      case 'date':
        return 'left';
      default:
        return 'center';
    }
  };

  const getActionsStackJustifyContent = () => {
    switch (alignActionsColumn) {
      case 'right':
        return 'flex-end';
      case 'left':
        return 'flex-start';
      case 'center':
        return 'center';
      default:
        return 'left';
    }
  };

  const translateIconCondition = (icons: any[], row: any) => {
    if (!icons || !Array.isArray(icons)) return icons;

    return icons.map((icon) => {
      if (icon.type === 'conditional' && typeof icon.condition === 'string') {
        let isMet = false;

        // Pattern 1: !['A', 'B'].includes(row.type)
        const notInPattern = /^!\s*\[(.*)\]\.includes\(row\.(.*)\)$/;
        // Pattern 2: ['A', 'B'].includes(row.type)
        const inPattern = /^\[(.*)\]\.includes\(row\.(.*)\)$/;

        if (notInPattern.test(icon.condition)) {
          const match = icon.condition.match(notInPattern);
          if (match) {
            const values = match[1]
              .replace(/'/g, '')
              .split(',')
              .map((v: string) => v.trim());
            const field = match[2];
            isMet = !values.includes(row[field]);
          }
        } else if (inPattern.test(icon.condition)) {
          const match = icon.condition.match(inPattern);
          if (match) {
            const values = match[1]
              .replace(/'/g, '')
              .split(',')
              .map((v: string) => v.trim());
            const field = match[2];
            isMet = values.includes(row[field]);
          }
        }

        return {
          ...icon,
          condition: isMet,
        };
      }
      return icon;
    });
  };

  const renderCellContent = (row: any, key: any): ReactNode => {
    const value = row[key];
    const columnType = columnMetadata[key]?.type;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      let renderValue = checkCellValueType(columnType, value);
      const pivotColumnType = meta?.columns?.[2]?.type;
      const suffix = columnMetadata[key]?.suffix;

      // Handle pivot table columns where type needs to be inferred from metadata
      if (!renderValue && meta.chartType === 'PIVOT_TABLE') {
        if (pivotColumnType) {
          renderValue = checkCellValueType(pivotColumnType, value);
        }
      }

      const icons = translateIconCondition(columnMetadata[key]?.icons, row);
      let leftIcon = null;
      let rightIcon = null;

      if (icons && Array.isArray(icons)) {
        icons.forEach((icon) => {
          if (icon.type === 'conditional' && Boolean(icon.condition)) {
            if (icon.position === 'left') {
              leftIcon = (
                <img
                  src={require(`../../assets/icons/${icon.location}`)}
                  alt="left_icon"
                  style={{ marginRight: '8px', userSelect: 'none' }}
                />
              );
            } else if (icon.position === 'right') {
              rightIcon = (
                <img
                  src={require(`../../assets/icons/${icon.location}`)}
                  alt="right_icon"
                  style={{ marginLeft: '8px', userSelect: 'none' }}
                />
              );
            }
          }
        });
      }

      if (value === 'null') {
        return (
          <span
            style={{
              width: '100%',
              minWidth: '3rem',
              display: 'flex',
              justifyContent: getCellValueAllignment(columnType),
            }}
          >
            N/A
          </span>
        );
      }

      return (
        <span
          style={{
            width: '100%',
            minWidth: columnType === 'currency' ? '6rem' : '3rem',
            display: 'flex',
            justifyContent: getCellValueAllignment(columnType),
            ...(columnType === 'currency' || pivotColumnType === 'currency'
              ? { whiteSpace: 'nowrap' }
              : {}),
          }}
        >
          {leftIcon}
          {renderValue ?? value}
          {suffix ? ` ${suffix}` : null}
          {rightIcon}
        </span>
      );
    }

    return (
      <span
        style={{
          width: '100%',
          minWidth: '3rem',
          display: 'flex',
          justifyContent: getCellValueAllignment(columnType),
        }}
      >
        -
      </span>
    );
  };

  const getRowCellBackGround = (index: number) => {
    if (depth && depth === index) {
      return '#FFFFFF';
    } else {
      return ROW_COLORS[index % 2 ? 0 : 1];
    }
  };

  const renderDynamicRows = (dynamicRows: Row<T>[]) => {
    return dynamicRows.map((row) => (
      <React.Fragment key={`${row.id}`}>
        <TableRow style={{ backgroundColor: '#FFFFFF' }}>
          {meta.chartType === 'MULTI_LEVEL_TABLE' && (
            <TableCell sx={{ border: 'none' }}></TableCell>
          )}
          {getLeafColumns.map((column, colindex) => (
            <TableCell
              key={String(column.key)}
              sx={{
                paddingY: '0.75rem',
                paddingLeft: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: 500,
                lineHeight: '1rem',
                color: '#1B1C17',
                border: '1px solid #2F736E1F',
                borderLeft: colindex === 0 ? '1px solid #2F736E1F' : 'none',
                borderRight: '1px solid #2F736E1F',
                backgroundColor: getCellBackground
                  ? getCellBackground(row[column.key], columnMetadata[column.key]) || '#FFFFFF'
                  : '#FFFFFF',
                borderTopLeftRadius: colindex === 0 ? '8px' : '0px',
                borderBottomLeftRadius: colindex === 0 ? '8px' : '0px',
                borderTopRightRadius: colindex === getLeafColumns.length - 1 ? '8px' : '0px',
                borderBottomRightRadius: colindex === getLeafColumns.length - 1 ? '8px' : '0px',
                ...(column.customCellStyles ? column.customCellStyles : {}),
              }}
            >
              {column.render
                ? column.render(row[column.key], row, 0)
                : renderCellContent(row, column.key)}
            </TableCell>
          ))}
        </TableRow>
      </React.Fragment>
    ));
  };

  const handleRowSelect = (id: string, isSelected: boolean) => {
    const newSelected = isSelected ? [...selectedRows, id] : selectedRows.filter((i) => i !== id);

    setSelectedRows(newSelected);
    if (setSelections) {
      setSelections(newSelected);
    }
  };

  const renderRows = (rows: Row<T>[], level = 0): ReactNode => {
    return rows.map((row, rowIndex) => {
      const isExpanded = expandedRows[row.id];
      const isUpdating = updatingRowId === row.id;
      const actionWithRenderUpdate = actions?.find((action) => action?.renderUpdateComponent);
      let isClickable: boolean;
      if (!isViewRowClickable) {
        isClickable = !!onViewRow;
      } else {
        isClickable = !!onViewRow && isViewRowClickable(row);
      }
      if (isUpdating && actionWithRenderUpdate) {
        return (
          <React.Fragment key={`${row.id}_${rowIndex}`}>
            <TableRow
              style={{
                backgroundColor: '#FFFFFF',
                cursor: isClickable ? 'pointer' : 'default',
                border: 'none',
                borderBottom: '1px solid #2F736E1F',
              }}
              onClick={() => {
                if (isClickable && onViewRow) onViewRow(row);
              }}
            >
              {meta.chartType === 'MULTI_LEVEL_TABLE' && (
                <TableCell
                  style={{
                    width: '5px',
                    backgroundColor: '#2F736E1F',
                    border: 'none',
                  }}
                ></TableCell>
              )}
              {getLeafColumns.map((column, colindex) => (
                <TableCell
                  key={String(column.key)}
                  sx={{
                    paddingY: '0.75rem',
                    paddingLeft: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    lineHeight: '1rem',
                    color: '#1B1C17',
                    border: 'none',
                    borderLeft: '1px solid #2F736E1F',
                    backgroundColor: getCellBackground
                      ? getCellBackground(row[column.key], columnMetadata[column.key]) ||
                        '#2F736E1F'
                      : '#2F736E1F',
                    ...(column.customCellStyles ? column.customCellStyles : {}),
                  }}
                >
                  {column.render
                    ? column.render(row[column.key], row, level)
                    : renderCellContent(row, column.key)}
                </TableCell>
              ))}
              {isShowActionColumn && level === 0 && (
                <TableCell
                  key={String(`${row.id}_${rowIndex}_actions`)}
                  sx={{
                    paddingY: '0.75rem',
                    border: 'none',
                    borderLeft: '1px solid #2F736E1F',
                    background: '#2F736E1F',
                    borderTopRightRadius: '8px',
                    borderBottomRightRadius: '8px',
                  }}
                ></TableCell>
              )}
            </TableRow>
            <TableRow key={`selected_${row.id}`}>
              <TableCell
                colSpan={getLeafColumns.length + 2}
                style={{
                  padding: 0,
                  backgroundColor: '#2F736E1F',
                  borderBottom: 'none',
                  borderRadius: '16px',
                }}
              >
                {actionWithRenderUpdate.renderUpdateComponent(row, () => setUpdatingRowId(null))}
              </TableCell>
            </TableRow>
          </React.Fragment>
        );
      }

      return (
        <React.Fragment key={`${row.id}_${rowIndex}`}>
          {/* Spacer Row */}
          {meta.chartType === 'MULTI_LEVEL_TABLE' &&
            depth &&
            depth > 0 &&
            level === 0 &&
            rowIndex > 0 && (
              <TableRow>
                <TableCell
                  colSpan={
                    getLeafColumns.length +
                    (meta.chartType === 'MULTI_LEVEL_TABLE' ? 1 : 0) +
                    (isShowActionColumn ? 1 : 0)
                  }
                  sx={{
                    padding: 0,
                    border: 'none',
                    height: '10px',
                    background: 'transparent',
                  }}
                />
              </TableRow>
            )}

          {/* Actual Row */}
          <TableRow
            style={{
              backgroundColor: rowIndex === rows.length - 1 && showTotal ? '#2F736E' : '#F0F0F1',
              cursor: isClickable ? 'pointer' : 'default',
            }}
            onClick={(e) => {
              // Don't trigger row click if clicking on checkbox
              if ((e.target as HTMLElement).closest('.MuiCheckbox-root')) {
                return;
              }
              if (isClickable && onViewRow) onViewRow(row);
            }}
          >
            {/* First cell checkbox and expand child  */}
            {meta.chartType === 'MULTI_LEVEL_TABLE' && (
              <TableCell
                style={{
                  width: '5px',
                  backgroundColor: level === 0 ? '#F0F0F1' : '#FFFFFF',
                  borderTopLeftRadius: level === 0 ? '8px' : '0px',
                  borderBottomLeftRadius:
                    (level === 0 && !isExpanded) || (level > 0 && rowIndex === rows?.length - 1)
                      ? '8px'
                      : '0px',
                  border: '1px solid #2F736E1F',
                }}
              >
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '7px',
                  }}
                >
                  {enableRowSelect && level === 0 && (
                    <Checkbox
                      checked={(selections || selectedRows).includes(row.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleRowSelect(row.id, e.target.checked);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        padding: '0px',
                      }}
                    />
                  )}
                  {row.children && (
                    <button
                      onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                        handleExpandRowClick(row.id, e);
                      }}
                      style={{
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      disabled={hideExpandIfNoChildren && row.children.length === 0}
                    >
                      <ArrowDropDownIcon
                        style={{
                          color:
                            hideExpandIfNoChildren && row.children.length === 0
                              ? '#827f7faa'
                              : '#162C36',
                          rotate: isExpanded ? '0deg' : '-90deg',
                        }}
                      />
                    </button>
                  )}
                  {!row.children && row.getChildren && (
                    <button
                      onClick={(e) => handleExpandDynamicChildrenRowClick(rowIndex, row, e)}
                      style={{
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <ArrowDropDownIcon
                        style={{
                          color: '#162C36',
                          rotate:
                            dynamicNestedRows?.index === rowIndex || isExpanded ? '0deg' : '-90deg',
                        }}
                      />
                    </button>
                  )}
                </span>
              </TableCell>
            )}

            {/* Actual cells/columns */}
            {getLeafColumns.map((column, colindex) => (
              <TableCell
                key={String(column.key)}
                sx={{
                  paddingY: '0.75rem',
                  paddingLeft: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: rowIndex === rows.length - 1 && showTotal ? 700 : 500,
                  lineHeight: '1rem',
                  color: rowIndex === rows.length - 1 && showTotal ? '#2F736E' : '#1B1C17',
                  border: '1px solid #2F736E1F',
                  backgroundColor: getCellBackground
                    ? getCellBackground(row[column.key], columnMetadata[column.key]) ||
                      getRowCellBackGround(level)
                    : getRowCellBackGround(level),
                  borderTopLeftRadius:
                    meta.chartType !== 'MULTI_LEVEL_TABLE' && colindex === 0 ? '8px' : '0px',
                  borderBottomLeftRadius:
                    meta.chartType !== 'MULTI_LEVEL_TABLE' && colindex === 0 ? '8px' : '0px',
                  borderTopRightRadius:
                    !isShowActionColumn && level === 0 && colindex === getLeafColumns.length - 1
                      ? '8px'
                      : '0px',
                  borderBottomRightRadius:
                    !isShowActionColumn &&
                    colindex === getLeafColumns.length - 1 &&
                    ((level === 0 && !isExpanded) || (level > 0 && rowIndex === rows?.length - 1))
                      ? '8px'
                      : '0px',
                  ...(column.customCellStyles ? column.customCellStyles : {}),
                }}
              >
                {column.render
                  ? column.render(row[column.key], row, level)
                  : renderCellContent(row, column.key)}
              </TableCell>
            ))}

            {/* Actions column */}
            {isShowActionColumn && (
              <TableCell
                key={String(`${row.id}_${rowIndex}_actions`)}
                sx={{
                  paddingY: '0.75rem',
                  paddingLeft: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  lineHeight: '1rem',
                  backgroundColor: level === 0 ? 'transparent' : '#FFFFFF',
                  textAlign: alignActionsColumn,
                  border: '1px solid #2F736E1F',
                  borderTopRightRadius: level === 0 ? '8px' : '0px',
                  borderBottomRightRadius:
                    (level === 0 && !isExpanded) || (level > 0 && rowIndex === rows?.length - 1)
                      ? '8px'
                      : '0px',
                }}
              >
                {(isSubLevelAction ? level !== 0 : level === 0) && (
                  <Stack
                    gap={'0.5rem'}
                    direction={'row'}
                    alignItems={'center'}
                    justifyContent={getActionsStackJustifyContent()}
                    sx={{ width: '100%' }}
                  >
                    {actions?.map((action, index) => {
                      if (action?.condition && !action.condition(row.id)) {
                        return <></>;
                      }
                      const isDisabled = action.disabled ? action.disabled(row.id) : false;
                      if (action.component) {
                        return React.cloneElement(action.component, {
                          key: String(`${row.id}_${rowIndex}_${index}_action`),
                          onClick: (e: Event) => {
                            if (isDisabled || !action.action) return;

                            action.action(row.id, e);
                          },
                          disabled: isDisabled,
                          style: {
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            opacity: isDisabled ? 0.5 : 1,
                          },
                        });
                      } else {
                        return (
                          <button
                            key={String(`${row.id}_${rowIndex}_${index}_action`)}
                            onClick={(e) => {
                              if (isDisabled) return;
                              handleRowActionClick(row.id, action);
                              e.stopPropagation();
                            }}
                            disabled={isDisabled}
                            style={{
                              border: 'none',
                              background: 'none',
                              cursor: isDisabled ? 'not-allowed' : 'pointer',
                              padding: 0,
                              marginLeft: alignActionsColumn === 'left' ? '0.5rem' : 0,
                              opacity: isDisabled ? 0.5 : 1,
                            }}
                          >
                            {action.icon && (
                              <img
                                src={require(`../../assets/icons/${action.icon}`)}
                                alt="action_icon"
                                style={{ userSelect: 'none' }}
                              />
                            )}
                            <Typography
                              fontSize={'0.875rem'}
                              fontWeight={600}
                              sx={{ color: isDisabled ? '#9E9E9E' : '#51B8B0' }}
                            >
                              {action.label}
                            </Typography>
                          </button>
                        );
                      }
                    })}
                  </Stack>
                )}
              </TableCell>
            )}
          </TableRow>
          {isExpanded && row.children && renderRows(row.children, level + 1)}
          {depth && dynamicNestedRows?.index === rowIndex && dynamicNestedRows?.dynamicRows?.length
            ? renderDynamicRows(dynamicNestedRows.dynamicRows)
            : null}
        </React.Fragment>
      );
    });
  };

  if (!data.length) {
    return (
      <TableContainer
        sx={hiddenScrollbarStyles}
        style={{
          width: '100%',
          height: '100%',
          marginBottom: '12px',
        }}
      >
        <Table
          sx={{
            borderSpacing: '0 10px',
            borderCollapse: 'separate',
          }}
        >
          <TableHead>
            <TableRow sx={{ borderRadius: '20px' }}>
              {meta?.chartType === 'MULTI_LEVEL_TABLE' && (
                <TableCell
                  rowSpan={hasSecondaryColumns ? 2 : 1}
                  sx={{
                    borderBottom: 'none',
                    background: '#2F736E',
                    borderTopLeftRadius: '8px',
                    WebkitBorderBottomLeftRadius: '8px',
                  }}
                >
                  <button
                    onClick={handleExpandAllRows}
                    style={{
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      color: '#6C6C6C',
                    }}
                  >
                    <ArrowDropDownIcon
                      style={{
                        color: '#9AD6D1',
                        rotate: allExpanded ? '0deg' : '-90deg',
                      }}
                    />
                  </button>
                </TableCell>
              )}
              {columns?.map((column, i) => (
                <TableCell
                  key={String(column.key)}
                  colSpan={column.secondaryColumns?.length || 1}
                  rowSpan={column.secondaryColumns?.length ? 1 : hasSecondaryColumns ? 2 : 1}
                  sx={{
                    paddingY: '0.75rem',
                    paddingLeft: '0.5rem',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    lineHeight: '1rem',
                    color: '#F6F8F9',
                    borderLeft: '1px solid #737F86',
                    borderBottom: 'none',
                    background: '#2F736E',
                    borderTopRightRadius:
                      i === columns.length - 1 && !isShowActionColumn ? '8px' : '0px',
                    borderBottomRightRadius:
                      i === columns.length - 1 && !isShowActionColumn && !hasSecondaryColumns
                        ? '8px'
                        : '0px',
                    borderTopLeftRadius:
                      meta.chartType !== 'MULTI_LEVEL_TABLE' && i === 0 ? '8px' : '0px',
                    WebkitBorderBottomLeftRadius:
                      meta.chartType !== 'MULTI_LEVEL_TABLE' && i === 0 ? '8px' : '0px',
                    textAlign: getCellValueAllignment(columnMetadata[column.key]?.type),
                    // Fill the table's row border-spacing gap between parent and sub-header rows
                    ...(column.secondaryColumns?.length ? { boxShadow: '0 10px 0 #2F736E' } : {}),
                    ...(column.customHeaderStyles ? column.customHeaderStyles : {}),
                  }}
                >
                  {column.renderHeader ? column.renderHeader(column) : column.label}
                </TableCell>
              ))}
              {isShowActionColumn && (
                <TableCell
                  rowSpan={hasSecondaryColumns ? 2 : 1}
                  sx={{
                    paddingY: '0.75rem',
                    paddingLeft: '0.5rem',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    lineHeight: '1rem',
                    color: '#F6F8F9',
                    borderLeft: '1px solid #737F86',
                    borderBottom: 'none',
                    background: '#2F736E',
                    borderTopRightRadius: '8px',
                    borderBottomRightRadius: '8px',
                    textAlign: alignActionsColumn,
                  }}
                >
                  Actions
                </TableCell>
              )}
            </TableRow>
            {/* Second header row for secondary columns */}
            {hasSecondaryColumns && (
              <TableRow>
                {columns?.map((column, i) =>
                  column.secondaryColumns?.map((subCol, subIdx) => {
                    const isLastSubCol =
                      i === columns.length - 1 && subIdx === column.secondaryColumns!.length - 1;
                    return (
                      <TableCell
                        key={String(subCol.key)}
                        sx={{
                          paddingY: '0.75rem',
                          paddingLeft: '0.5rem',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          lineHeight: '1rem',
                          color: '#F6F8F9',
                          borderLeft: '1px solid #737F86',
                          borderBottom: 'none',
                          background: '#2F736E',
                          borderTop: '1px solid #737F86',
                          borderBottomRightRadius:
                            isLastSubCol && !isShowActionColumn ? '8px' : '0px',
                          textAlign: getCellValueAllignment(columnMetadata[subCol.key]?.type),
                          ...(subCol.customHeaderStyles ? subCol.customHeaderStyles : {}),
                        }}
                      >
                        {subCol.renderHeader ? subCol.renderHeader(subCol) : subCol.label}
                      </TableCell>
                    );
                  }),
                )}
              </TableRow>
            )}
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={(actions?.length || 0) + (getLeafColumns?.length || 0)}
                  align="center"
                >
                  {Array.from(new Array(5)).map((_, index) => (
                    <Skeleton
                      key={index}
                      variant="rectangular"
                      width="100%"
                      height="1rem"
                      sx={{
                        borderRadius: '0.25rem',
                        marginBottom: '0.5rem',
                      }}
                    />
                  ))}
                </TableCell>
              </TableRow>
            ) : (
              <TableRow>
                <TableCell
                  colSpan={
                    actions?.length ? getLeafColumns.length + actions.length : getLeafColumns.length
                  }
                  align="center"
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      py: 1,
                    }}
                  >
                    <img src={noDataIcon} alt="no-data" style={{ height: '80px', width: '80px' }} />
                    <Typography fontSize={'0.875rem'} fontWeight={500} sx={{ color: '#6C6C6C' }}>
                      No data available
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <>
      <TableContainer
        sx={hiddenScrollbarStyles}
        style={{
          width: '100%',
          height: '100%',
          marginBottom: '12px',
          maxHeight: isHeightRestricted ? '35rem' : 'none',
        }}
      >
        <Table
          stickyHeader={isHeightRestricted}
          sx={{
            borderSpacing: depth && depth > 0 ? '0 0' : '0 10px',
            borderCollapse: 'separate',
            transform: isHeightRestricted ? 'translateY(-10px)' : undefined,
          }}
        >
          <TableHead
            sx={
              isHeightRestricted
                ? {
                    position: 'sticky',
                    top: '10px',
                    zIndex: 2,
                  }
                : undefined
            }
          >
            <TableRow sx={{ borderRadius: '20px' }}>
              {meta.chartType === 'MULTI_LEVEL_TABLE' && (
                <TableCell
                  rowSpan={hasSecondaryColumns ? 2 : 1}
                  sx={{
                    borderBottom: 'none',
                    background: '#2F736E',
                    borderTopLeftRadius: '8px',
                    WebkitBorderBottomLeftRadius: '8px',
                  }}
                >
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '7px',
                    }}
                  >
                    {enableRowSelect && (
                      <Checkbox
                        indeterminate={
                          (selections || selectedRows).length > 0 &&
                          (selections || selectedRows).length < data.length
                        }
                        checked={
                          data.length > 0 && (selections || selectedRows).length === data.length
                        }
                        onChange={(e) => {
                          e.stopPropagation();
                          if (e.target.checked) {
                            const allIds = data.map((r) => r.id);
                            setSelectedRows(allIds);
                            if (setSelections) {
                              setSelections(allIds);
                            }
                          } else {
                            setSelectedRows([]);
                            if (setSelections) {
                              setSelections([]);
                            }
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          color: '#9AD6D1',
                          padding: '0px',
                          '&.Mui-checked': {
                            color: '#9AD6D1',
                          },
                          '&.MuiCheckbox-indeterminate': {
                            color: '#9AD6D1',
                          },
                          '&.MuiCheckbox-root.Mui-checked, &.MuiCheckbox-root.MuiCheckbox-indeterminate':
                            {
                              color: '#9AD6D1',
                            },
                          '&:hover': {
                            backgroundColor: 'rgba(154, 214, 209, 0.08)',
                          },
                        }}
                      />
                    )}
                    <button
                      onClick={handleExpandAllRows}
                      style={{
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        color: '#6C6C6C',
                        marginTop: '4px',
                      }}
                    >
                      <ArrowDropDownIcon
                        style={{
                          color: '#9AD6D1',
                          rotate: allExpanded ? '0deg' : '-90deg',
                        }}
                      />
                    </button>
                  </span>
                </TableCell>
              )}
              {columns.map((column, i) => (
                <TableCell
                  key={String(column.key)}
                  colSpan={column.secondaryColumns?.length || 1}
                  rowSpan={column.secondaryColumns?.length ? 1 : hasSecondaryColumns ? 2 : 1}
                  sx={{
                    paddingY: '0.75rem',
                    paddingLeft: '0.5rem',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    lineHeight: '1rem',

                    color: '#F6F8F9',
                    borderLeft: '1px solid #737F86',
                    borderBottom: 'none',
                    background: '#2F736E',
                    borderTopRightRadius:
                      i === columns.length - 1 && !isShowActionColumn ? '8px' : '0px',
                    borderBottomRightRadius:
                      i === columns.length - 1 && !isShowActionColumn && !hasSecondaryColumns
                        ? '8px'
                        : '0px',
                    borderTopLeftRadius:
                      meta.chartType !== 'MULTI_LEVEL_TABLE' && i === 0 ? '8px' : '0px',
                    WebkitBorderBottomLeftRadius:
                      meta.chartType !== 'MULTI_LEVEL_TABLE' && i === 0 ? '8px' : '0px',
                    textAlign: getCellValueAllignment(columnMetadata[column.key]?.type),
                    // Fill the table's row border-spacing gap between parent and sub-header rows
                    ...(column.secondaryColumns?.length ? { boxShadow: '0 10px 0 #2F736E' } : {}),
                    ...(column.customHeaderStyles ? column.customHeaderStyles : {}),
                  }}
                >
                  {column.renderHeader ? column.renderHeader(column) : column.label}
                </TableCell>
              ))}
              {isShowActionColumn && (
                <TableCell
                  rowSpan={hasSecondaryColumns ? 2 : 1}
                  sx={{
                    paddingY: '0.75rem',
                    paddingLeft: '0.5rem',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    lineHeight: '1rem',
                    color: '#F6F8F9',
                    borderLeft: '1px solid #737F86',
                    borderBottom: 'none',
                    background: '#2F736E',
                    borderTopRightRadius: '8px',
                    borderBottomRightRadius: '8px',
                    textAlign: alignActionsColumn,
                  }}
                >
                  Actions
                </TableCell>
              )}
            </TableRow>
            {/* Second header row for secondary columns */}
            {hasSecondaryColumns && (
              <TableRow>
                {columns?.map((column, i) =>
                  column.secondaryColumns?.map((subCol, subIdx) => {
                    const isLastSubCol =
                      i === columns.length - 1 && subIdx === column.secondaryColumns!.length - 1;
                    return (
                      <TableCell
                        key={String(subCol.key)}
                        sx={{
                          paddingY: '0.75rem',
                          paddingLeft: '0.5rem',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          lineHeight: '1rem',
                          color: '#F6F8F9',
                          borderLeft: '1px solid #737F86',
                          borderBottom: 'none',
                          background: '#2F736E',
                          borderTop: '1px solid #737F86',
                          borderBottomRightRadius:
                            isLastSubCol && !isShowActionColumn ? '8px' : '0px',
                          textAlign: getCellValueAllignment(columnMetadata[subCol.key]?.type),
                          ...(subCol.customHeaderStyles ? subCol.customHeaderStyles : {}),
                        }}
                      >
                        {subCol.renderHeader ? subCol.renderHeader(subCol) : subCol.label}
                      </TableCell>
                    );
                  }),
                )}
              </TableRow>
            )}

            {/* Spacer Row */}
            {meta.chartType === 'MULTI_LEVEL_TABLE' && depth && depth > 0 && (
              <TableRow>
                <TableCell
                  colSpan={
                    getLeafColumns.length +
                    (meta.chartType === 'MULTI_LEVEL_TABLE' ? 1 : 0) +
                    (isShowActionColumn ? 1 : 0)
                  }
                  sx={{
                    padding: 0,
                    border: 'none',
                    height: '10px',
                    background: 'transparent',
                  }}
                />
              </TableRow>
            )}
          </TableHead>
          <TableBody>{renderRows(data)}</TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

export default GenericTable;
